import { type CSSProperties, type ReactNode, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { Unstable_TrapFocus as FocusTrap } from '@mui/material';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { ZIndex } from '@/consts/z-indexes.consts';
import { TextLgSb, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { useTheme } from '@/UI/theme.exports';
import { WeaveThemeProvider } from '@/UI/WeaveThemeProvider/WeaveThemeProvider';

// Id of the app-content sibling this overlay marks `inert` while interactive; main.tsx stamps it on the
// producing element. Shared so a rename can't silently no-op the inert.
export const WAF_APP_CONTENT_ROOT_ID = 'waf-app-content-root';

// Test id on the overlay's outermost element, which is the invisible dock while docked and the backdrop while
// interactive. Shared so the source and the tests that query it can't drift apart.
export const WAF_OVERLAY_ROOT_TEST_ID = 'waf-overlay-root';

// Test id on the interactive card (the role="dialog"); shared for the same reason.
export const WAF_INTERACTIVE_CARD_TEST_ID = 'waf-interactive-card';

const INVISIBLE_DOCK_STYLE: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  right: 0,
  width: 0,
  height: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
};

const BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: color.Black84_T,
  zIndex: ZIndex.Overlays.CRITICAL_SYSTEM_ALERT,
  pointerEvents: 'auto',
};

const CARD_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 24,
  minWidth: 320,
  maxWidth: 400,
  background: token.bg,
  borderRadius: 13,
};

interface WAFInteractiveChallengeOverlayProps {
  isInteractive: boolean;
  title: string;
  body: string;
  /** The live challenge widget; its DOM identity must stay stable across the promotion transform. */
  children: ReactNode;
  /** Rendered under the widget only while interactive, so it mounts on each promotion. */
  help?: ReactNode;
}

/**
 * Presentation transform for an always-mounted challenge widget: an invisible off-screen dock while
 * `isInteractive` is false, promoted in place to a labelled, focus-trapped modal dialog when true.
 * The promotion never remounts `children`, so a live in-flight challenge survives it.
 */
export function WAFInteractiveChallengeOverlay(props: WAFInteractiveChallengeOverlayProps) {
  return (
    // Mounted above the app's own WeaveThemeProvider (inside the Router), so it wraps itself in one; otherwise
    // the DS typography renders against MUI's default theme in production.
    // disableCssBaseline: this provider is always mounted and lives outside PostHogProvider, so a CssBaseline
    // from it would re-write :root/html/body app-wide with flags-unresolved, wrong-vars styling. The card is
    // self-sufficient (literal colors plus the theme font set on it below), so it needs no global baseline.
    <WeaveThemeProvider disableCssBaseline>
      <WAFInteractiveChallengeOverlayContent {...props} />
    </WeaveThemeProvider>
  );
}

function WAFInteractiveChallengeOverlayContent({
  isInteractive,
  title,
  body,
  children,
  help,
}: WAFInteractiveChallengeOverlayProps) {
  const titleId = useId();
  const bodyId = useId();
  const hostRef = useRef<HTMLDivElement>(null);
  // The MUI typography variants carry no fontFamily and this overlay skips the global CssBaseline that would
  // supply it, so set the DS font from the theme on the card; otherwise the title/body fall back to the UA
  // serif in the sign-in auth-gate window, where the app mounts no CssBaseline of its own either.
  const theme = useTheme();

  // A layout effect so its cleanup (removing `inert`) runs in the commit phase, BEFORE the child FocusTrap's
  // passive cleanup restores focus — otherwise restore would target a still-inert node and focus falls to <body>.
  useLayoutEffect(() => {
    if (!isInteractive) return;

    // Resolve the body-level ancestor that CONTAINS this overlay first. A detached host leaves it null; bail
    // then so we can never fall into inerting every body child (the whole page).
    let overlayBodyAncestor: Element | null = hostRef.current;
    while (overlayBodyAncestor && overlayBodyAncestor.parentElement !== document.body) {
      overlayBodyAncestor = overlayBodyAncestor.parentElement;
    }
    if (!overlayBodyAncestor) return;

    const inerted: Element[] = [];
    const markInert = (element: Element | null): void => {
      // Never inert the subtree that holds this overlay (its body-ancestor, or defensively any element that
      // contains the host — `contains` is reflexive, so this also covers the body-ancestor itself). Inerting it
      // would make the challenge unsolvable.
      if (element && !element.contains(hostRef.current) && !element.hasAttribute('inert')) {
        element.setAttribute('inert', '');
        inerted.push(element);
      }
    };
    // A MUI modal sets aria-hidden on every body child except its own portal — including the root holding this
    // overlay — so our card would sit in an aria-hidden subtree AT skips. Clear it on each ancestor up to <body>
    // and never restore it: MUI's ModalManager drops aria-hidden when its modal closes without re-clearing
    // pre-hidden siblings, so restoring on demote could re-hide #root with nothing left to remove it. A
    // stale-absent aria-hidden is soft and MUI-self-correcting; a stale-present one is the hard
    // permanent-silence failure, so prefer clear-only.
    const clearAncestorAriaHidden = (): void => {
      for (
        let ancestor: Element | null = hostRef.current;
        ancestor && ancestor !== document.body;
        ancestor = ancestor.parentElement
      ) {
        ancestor.removeAttribute('aria-hidden');
      }
    };

    // The app content shares this overlay's own body-ancestor, so inert it directly.
    markInert(document.getElementById(WAF_APP_CONTENT_ROOT_ID));
    // MUI dialogs/menus/selects portal to document.body, escaping the app-content subtree, and their own
    // FocusTrap ping-pongs with ours. Inert every current body child except the one that holds this overlay.
    for (const bodyChild of Array.from(document.body.children)) markInert(bodyChild);
    clearAncestorAriaHidden();

    // The sweep above is a one-shot snapshot; a body portal opened DURING the interactive window (an async
    // toast/dialog, a socket-driven modal) escapes it, so inert new body children as they mount and re-clear the
    // ancestor aria-hidden a freshly-opened MUI modal would have re-applied. childList-only observation, so the
    // aria-hidden removals can't re-trigger it.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const added of mutation.addedNodes) {
          if (added instanceof Element) markInert(added);
        }
      }
      clearAncestorAriaHidden();
    });
    observer.observe(document.body, { childList: true });

    return () => {
      observer.disconnect();
      for (const element of inerted) element.removeAttribute('inert');
    };
  }, [isInteractive]);

  // `inert` blocks focus/pointer WITHIN the inerted app, but events that originate in the (non-inert) overlay
  // still propagate through document/window to the app's global listeners — including FlowGraph's capture-phase
  // keydown and its document.body pointer handlers, which would run destructive designer shortcuts or keep
  // dragging a node under the modal on modal input. Stop overlay-originating key/pointer events at
  // window-capture (which precedes document-capture) for the whole interactive window; both edges of each
  // gesture (down/up, keydown/keyup, move/cancel, dblclick) so an in-flight drag can't continue and no up/keyup leaks.
  // NOTE: stopping at window-capture also pre-empts MUI FocusTrap's document-capture loopFocus, so backward-Tab
  // wrapping is unenforced (the app behind is inert, so focus still cannot land there) and any React
  // onKeyDown/onPointer* inside the card is dead code; controls in the card must act on `click`, which is not
  // stopped.
  useEffect(() => {
    if (!isInteractive) return;
    const stopIfInOverlay = (event: Event): void => {
      const target = event.target;
      if (!(target instanceof Node) || !hostRef.current?.contains(target)) return;
      event.stopPropagation();
    };
    const eventTypes = [
      'keydown',
      'keyup',
      'pointerdown',
      'pointermove',
      'pointerup',
      'pointercancel',
      'dblclick',
    ] as const;
    for (const eventType of eventTypes) window.addEventListener(eventType, stopIfInOverlay, true);
    return () => {
      for (const eventType of eventTypes) window.removeEventListener(eventType, stopIfInOverlay, true);
    };
  }, [isInteractive]);

  return (
    <div
      ref={hostRef}
      data-testid={WAF_OVERLAY_ROOT_TEST_ID}
      // Hide the 0×0 Turnstile iframe from AT while docked; absent when interactive so the promoted dialog
      // is announced.
      aria-hidden={isInteractive ? undefined : true}
      style={isInteractive ? BACKDROP_STYLE : INVISIBLE_DOCK_STYLE}
    >
      {/* Trap wraps the CARD (the role="dialog"), so promotion lands focus inside the dialog; `open` only
      while interactive means it is inert and never steals focus into the dock. */}
      <FocusTrap open={isInteractive}>
        <div
          data-testid={WAF_INTERACTIVE_CARD_TEST_ID}
          role={isInteractive ? 'dialog' : undefined}
          aria-modal={isInteractive ? true : undefined}
          aria-labelledby={isInteractive ? titleId : undefined}
          aria-describedby={isInteractive ? bodyId : undefined}
          tabIndex={isInteractive ? -1 : undefined}
          style={isInteractive ? { ...CARD_STYLE, fontFamily: theme.typography.fontFamily } : undefined}
        >
          {isInteractive && (
            <>
              <TextLgSb id={titleId} color={token.text}>
                {title}
              </TextLgSb>
              <TextStdRg id={bodyId}>{body}</TextStdRg>
            </>
          )}
          {/* The slot node stays stable across promote/demote via positional reconciliation (same element,
          same tree position); the key just marks that load-bearing invariant, it is not what enforces it. */}
          <div key="waf-widget-slot">{children}</div>
          {isInteractive && help}
        </div>
      </FocusTrap>
    </div>
  );
}
